"use client";

import { useEffect, startTransition, useContext, useMemo, useRef, useState } from "react";
import { useSchema } from "@/contexts/FormSchemaContext";
import { FormContext } from "@/contexts/FormDataContext";
import { useFormUtils } from "@/hooks/useFormUtils";
import { Aside, FormWrapper, LoadingWrapper } from "./FormLayout";
import Loading from "../atoms/Loading";
import Requirements from "./Requirements";
import Stepper from "./Stepper";
import Step from "./Step";
import Summary from "./Summary";
import ContactCard from "./ContactCard";
import OrderSummary from "./OrderSummary";
import Button from "../atoms/Button";
import useMediaQuery from "@/hooks/useMediaQuery";
import { useScrollToHeader } from "@/hooks/useScrollToHeader";
import { useTranslation } from "@/hooks/useTranslation";
import classNames from "classnames";

const MultiStepForm = ({
  formSlug,
  summary,
  formData,
}: {
  formSlug: string;
  summary?: boolean;
  formData?: any;
}) => {
  const { schemaSlug, setSchemaSlug, formSchema } = useSchema();
  const { loadingFromApi } = useContext(FormContext)!;

  useEffect(() => {
    if (formSlug && schemaSlug !== formSlug) {
      // startTransition: Assigns low priority, avoiding cascading renders
      // and blocking other elements from loading.
      startTransition(() => {
        setSchemaSlug(formSlug);
      });
    }
  }, [formSlug, schemaSlug, setSchemaSlug]);

  if (!formSchema || loadingFromApi) return <FormLoading />;
  if (summary) return <FormSummary data={formData} />;
  return <FormStep formData={formData} />;
};

const FormStep = ({ formData }: { formData: any }) => {
  const { redirectToFormIsNotValid, isEditingForm } = useFormUtils();

  useEffect(() => {
    if (isEditingForm) redirectToFormIsNotValid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingForm]);

  return (
    <>
      <Stepper />
      <FormWrapper>
        <Step />
        <Aside>
          {formData && <Requirements data={formData} />}
          <ContactCard />
        </Aside>
      </FormWrapper>
    </>
  );
};

const FormSummary = ({ data }: { data: any }) => {
  const { t, language } = useTranslation();
  const {
    validatedForm,
    formData,
    formDetails,
    selectedMethod,
    servicesDetails,
    proofOfTravelError,
  } = useContext(FormContext)!;
  const { redirectToFormIsNotValid, handleSendReviewedForm } = useFormUtils();
  const isMobile = useMediaQuery("mobile");
  const formName = formDetails?.pdf?.name;
  const hasServices = servicesDetails?.length > 0;
  const inlineSubmitRef = useRef<HTMLDivElement>(null);
  const [showFixed, setShowFixed] = useState(false);

  // Resolve the selected paid service once and reuse it across the summary snapshot.
  const selectedService = useMemo(
    () => servicesDetails?.find((service: any) => service.pdf?.name === selectedMethod),
    [servicesDetails, selectedMethod]
  );

  // Build a single source of truth for pricing data so UI and tracking read the same state.
  const selectedFormDetails = useMemo(() => {
    const sourceDetails = selectedService || formDetails || {};
    return {
      pdf: sourceDetails.pdf,
      price: sourceDetails.price,
      fees: sourceDetails.pdf?.government_fee,
      total: sourceDetails.total,
    };
  }, [selectedService, formDetails]);

  // When smart/expedited is selected, hide card and priority addons to keep totals consistent.
  const hideCardAndPriorityAddons = selectedMethod !== "form" && hasServices;

  useEffect(() => {
    redirectToFormIsNotValid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useScrollToHeader();

  useEffect(() => {
    if (!isMobile) {
      setShowFixed(false);
      return;
    }

    const el = inlineSubmitRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const viewportBottom = entry.rootBounds?.bottom ?? window.innerHeight;
        // Only show fixed while inline buttons are still below the viewport
        setShowFixed(entry.boundingClientRect.top >= viewportBottom);
      },
      { threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile]);

  const fixedSubmitClassNames = classNames(
    "orderSummary__submit-button",
    "orderSummary__submit-button--fixedMobile",
    {
      "orderSummary__submit-button--fixedMobileHidden": !showFixed,
    }
  );

  return (
    <>
      {!validatedForm && <FormLoading />}
      <Stepper completed />
      <FormWrapper>
        <Summary />
        <Aside>
          <OrderSummary
            addons={formData.addons}
            pdf={selectedFormDetails.pdf}
            price={selectedFormDetails.pdf?.price?.amount}
            fees={selectedFormDetails.pdf?.price?.government_fee}
            fixed
            form
            isOpen
            selectedMethod={selectedMethod}
            hideCardAndPriorityAddons={hideCardAndPriorityAddons}
            formName={formName}
          />
          {isMobile && (
            <>
              <div
                ref={inlineSubmitRef}
                className="orderSummary__submit-button">
                <Button
                  primary
                  buttonId="submit-application"
                  dataQa={`submit-application`}
                  label={t("Continue")}
                  onClick={handleSendReviewedForm}
                  disabled={proofOfTravelError}
                  full
                />
              </div>
              <div className={fixedSubmitClassNames}>
                <Button
                  primary
                  dataQa={`submit-application-fixed`}
                  label={t("Continue")}
                  onClick={handleSendReviewedForm}
                  disabled={proofOfTravelError}
                  full
                />
              </div>
            </>
          )}
          {data && <Requirements data={data} />}
          <ContactCard />
        </Aside>
      </FormWrapper>
    </>
  );
};

export const FormLoading = () => (
  <LoadingWrapper>
    <Loading />
  </LoadingWrapper>
);

export default MultiStepForm;
